import { authApi, setAuthToken, clearAuthToken } from './api';

const AUTH_SET_SESSION = 'auth/SET_SESSION';
const AUTH_SET_ADMIN_SESSION = 'auth/SET_ADMIN_SESSION';
const AUTH_CLEAR_SESSION = 'auth/CLEAR_SESSION';
const AUTH_OPEN_VIEW = 'auth/OPEN_VIEW';
const AUTH_CLOSE_VIEW = 'auth/CLOSE_VIEW';
const AUTH_HYDRATE = 'auth/HYDRATE';
const AUTH_SET_MODE = 'auth/SET_MODE';
const REFRESH_SKEW_MS = 60 * 1000;

const initialState = {
  user: null,
  token: null,
  tokenExpiresAt: null,
  refreshToken: null,
  refreshTokenExpiresAt: null,
  authViewVisible: false,
  authViewMode: 'login',
  isHydrated: false,
  admin: {
    user: null,
    token: null,
    tokenExpiresAt: null,
    refreshToken: null,
    refreshTokenExpiresAt: null,
  },
};

function normalizeTokenValue(token) {
  if (!token) {
    return null;
  }

  if (typeof token === 'string') {
    const trimmed = token.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
      return null;
    }
    return trimmed;
  }

  if (typeof token === 'object') {
    if (token.token !== undefined) {
      return normalizeTokenValue(token.token);
    }
    if (token.accessToken !== undefined) {
      return normalizeTokenValue(token.accessToken);
    }
  }

  return null;
}

function normalizePersistedToken(raw) {
  if (!raw) {
    return null;
  }

  if (typeof raw !== 'string') {
    return normalizeTokenValue(raw);
  }

  const trimmed = raw.trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    const normalized = normalizeTokenValue(parsed);
    if (normalized) {
      return normalized;
    }
  } catch (error) {
    // Ignore JSON parse errors; fall back to raw string handling.
  }

  return normalizeTokenValue(trimmed);
}

function parseJwtExpiresAt(token) {
  const normalized = normalizeTokenValue(token);
  if (!normalized) {
    return null;
  }
  if (typeof window === 'undefined' || !window.atob) {
    return null;
  }
  const parts = normalized.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(window.atob(padded));
    return payload && payload.exp ? new Date(payload.exp * 1000).toISOString() : null;
  } catch (error) {
    return null;
  }
}

function normalizeTokenExpiresAt(token, fallback) {
  if (token && typeof token === 'object' && token.expiresAt) {
    return token.expiresAt;
  }
  if (fallback) {
    return fallback;
  }
  return parseJwtExpiresAt(token);
}

function isTokenExpiring(expiresAt, skewMs = REFRESH_SKEW_MS) {
  if (!expiresAt) {
    return false;
  }
  const expiresTime = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiresTime)) {
    return false;
  }
  return expiresTime - Date.now() <= skewMs;
}

function isTokenExpired(expiresAt) {
  return isTokenExpiring(expiresAt, 0);
}

function persistValue(key, value) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
    } else if (typeof value === 'object') {
      window.localStorage.setItem(key, JSON.stringify(value));
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (error) {
    // Swallow persistence errors (private browsing, etc.)
  }
}

function extractResponseData(response) {
  if (!response) {
    return null;
  }

  if (response.data) {
    return response.data;
  }

  return response;
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case AUTH_SET_SESSION:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        tokenExpiresAt: action.payload.tokenExpiresAt,
        refreshToken: action.payload.refreshToken,
        refreshTokenExpiresAt: action.payload.refreshTokenExpiresAt,
        authViewVisible: false,
        authViewMode: 'login',
      };
    case AUTH_SET_ADMIN_SESSION:
      return {
        ...state,
        admin: {
          user: action.payload.user,
          token: action.payload.token,
          tokenExpiresAt: action.payload.tokenExpiresAt,
          refreshToken: action.payload.refreshToken,
          refreshTokenExpiresAt: action.payload.refreshTokenExpiresAt,
        },
      };
    case AUTH_CLEAR_SESSION:
      return {
        ...state,
        user: null,
        token: null,
        tokenExpiresAt: null,
        refreshToken: null,
        refreshTokenExpiresAt: null,
        authViewVisible: false,
        authViewMode: 'login',
        admin: {
          user: null,
          token: null,
          tokenExpiresAt: null,
          refreshToken: null,
          refreshTokenExpiresAt: null,
        },
      };
    case AUTH_OPEN_VIEW:
      return {
        ...state,
        authViewVisible: true,
        authViewMode: action.payload || 'login',
      };
    case AUTH_CLOSE_VIEW:
      return {
        ...state,
        authViewVisible: false,
      };
    case AUTH_SET_MODE:
      return {
        ...state,
        authViewMode: action.payload || 'login',
      };
    case AUTH_HYDRATE:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        tokenExpiresAt: action.payload.tokenExpiresAt || null,
        refreshToken: action.payload.refreshToken || null,
        refreshTokenExpiresAt: action.payload.refreshTokenExpiresAt || null,
        admin: action.payload.admin || state.admin,
        authViewVisible: false,
        authViewMode: action.payload.authViewMode || 'login',
        isHydrated: true,
      };
    default:
      return state;
  }
}

export const setSession = (user, token, refreshToken) => (dispatch) => {
  const normalizedToken = normalizeTokenValue(token);
  const normalizedRefreshToken = normalizeTokenValue(refreshToken);
  const tokenExpiresAt = normalizeTokenExpiresAt(token);
  const refreshTokenExpiresAt = normalizeTokenExpiresAt(refreshToken);
  if (normalizedToken) {
    setAuthToken(normalizedToken);
  } else {
    clearAuthToken();
  }
  persistValue('asi:user', user || null);
  persistValue('asi:token', normalizedToken || null);
  persistValue('asi:token-expires-at', tokenExpiresAt || null);
  persistValue('asi:refresh-token', normalizedRefreshToken || null);
  persistValue('asi:refresh-token-expires-at', refreshTokenExpiresAt || null);
  dispatch({
    type: AUTH_SET_SESSION,
    payload: {
      user,
      token: normalizedToken,
      tokenExpiresAt,
      refreshToken: normalizedRefreshToken,
      refreshTokenExpiresAt,
    },
  });
};

export const setAdminSession = (user, token, refreshToken) => (dispatch) => {
  const normalizedToken = normalizeTokenValue(token);
  const normalizedRefreshToken = normalizeTokenValue(refreshToken);
  const tokenExpiresAt = normalizeTokenExpiresAt(token);
  const refreshTokenExpiresAt = normalizeTokenExpiresAt(refreshToken);
  persistValue('asi:admin-user', user || null);
  persistValue('asi:admin-token', normalizedToken || null);
  persistValue('asi:admin-token-expires-at', tokenExpiresAt || null);
  persistValue('asi:admin-refresh-token', normalizedRefreshToken || null);
  persistValue('asi:admin-refresh-token-expires-at', refreshTokenExpiresAt || null);
  dispatch({
    type: AUTH_SET_ADMIN_SESSION,
    payload: {
      user,
      token: normalizedToken,
      tokenExpiresAt,
      refreshToken: normalizedRefreshToken,
      refreshTokenExpiresAt,
    },
  });
};

export const clearSession = () => (dispatch) => {
  persistValue('asi:user', null);
  persistValue('asi:token', null);
  persistValue('asi:token-expires-at', null);
  persistValue('asi:refresh-token', null);
  persistValue('asi:refresh-token-expires-at', null);
  persistValue('asi:admin-user', null);
  persistValue('asi:admin-token', null);
  persistValue('asi:admin-token-expires-at', null);
  persistValue('asi:admin-refresh-token', null);
  persistValue('asi:admin-refresh-token-expires-at', null);
  clearAuthToken();
  dispatch({ type: AUTH_CLEAR_SESSION });
};

export const openAuthView = (mode = 'login') => ({
  type: AUTH_OPEN_VIEW,
  payload: mode,
});

export const closeAuthView = () => ({
  type: AUTH_CLOSE_VIEW,
});

export const setAuthViewMode = (mode = 'login') => ({
  type: AUTH_SET_MODE,
  payload: mode,
});

export const hydrateSession = () => (dispatch) => {
  if (typeof window === 'undefined') {
    dispatch({
      type: AUTH_HYDRATE,
      payload: { user: null, token: null },
    });
    return;
  }

  try {
    const storedToken = window.localStorage.getItem('asi:token');
    const storedTokenExpiresAt = window.localStorage.getItem('asi:token-expires-at');
    const storedRefreshToken = window.localStorage.getItem('asi:refresh-token');
    const storedRefreshTokenExpiresAt = window.localStorage.getItem('asi:refresh-token-expires-at');
    const storedUserRaw = window.localStorage.getItem('asi:user');
    const adminToken = window.localStorage.getItem('asi:admin-token');
    const adminTokenExpiresAt = window.localStorage.getItem('asi:admin-token-expires-at');
    const adminRefreshToken = window.localStorage.getItem('asi:admin-refresh-token');
    const adminRefreshTokenExpiresAt = window.localStorage.getItem('asi:admin-refresh-token-expires-at');
    const adminUserRaw = window.localStorage.getItem('asi:admin-user');

    const normalizedToken = normalizePersistedToken(storedToken);
    const normalizedRefreshToken = normalizePersistedToken(storedRefreshToken);
    const tokenExpiresAt = normalizeTokenExpiresAt(normalizedToken, storedTokenExpiresAt);
    const refreshTokenExpiresAt = normalizeTokenExpiresAt(normalizedRefreshToken, storedRefreshTokenExpiresAt);

    let user = null;
    if (storedToken && storedUserRaw) {
      try {
        user = JSON.parse(storedUserRaw);
      } catch (error) {
        persistValue('asi:user', null);
      }
    }

    let adminUser = null;
    const normalizedAdminToken = normalizePersistedToken(adminToken);
    const normalizedAdminRefreshToken = normalizePersistedToken(adminRefreshToken);
    const normalizedAdminTokenExpiresAt = normalizeTokenExpiresAt(normalizedAdminToken, adminTokenExpiresAt);
    const normalizedAdminRefreshTokenExpiresAt = normalizeTokenExpiresAt(
      normalizedAdminRefreshToken,
      adminRefreshTokenExpiresAt,
    );
    if (adminToken && adminUserRaw) {
      try {
        adminUser = JSON.parse(adminUserRaw);
      } catch (error) {
        persistValue('asi:admin-user', null);
      }
    }

    if (normalizedToken && normalizedToken !== storedToken) {
      persistValue('asi:token', normalizedToken);
    } else if (storedToken && !normalizedToken) {
      persistValue('asi:token', null);
    }
    persistValue('asi:token-expires-at', tokenExpiresAt || null);
    persistValue('asi:refresh-token', normalizedRefreshToken || null);
    persistValue('asi:refresh-token-expires-at', refreshTokenExpiresAt || null);

    if (normalizedAdminToken && normalizedAdminToken !== adminToken) {
      persistValue('asi:admin-token', normalizedAdminToken);
    } else if (adminToken && !normalizedAdminToken) {
      persistValue('asi:admin-token', null);
    }
    persistValue('asi:admin-token-expires-at', normalizedAdminTokenExpiresAt || null);
    persistValue('asi:admin-refresh-token', normalizedAdminRefreshToken || null);
    persistValue('asi:admin-refresh-token-expires-at', normalizedAdminRefreshTokenExpiresAt || null);

    if (normalizedToken && user) {
      setAuthToken(normalizedToken);
    } else {
      clearAuthToken();
    }

    dispatch({
      type: AUTH_HYDRATE,
      payload: {
        user,
        token: normalizedToken || null,
        tokenExpiresAt,
        refreshToken: normalizedRefreshToken || null,
        refreshTokenExpiresAt,
        admin: {
          user: adminUser,
          token: normalizedAdminToken || null,
          tokenExpiresAt: normalizedAdminTokenExpiresAt,
          refreshToken: normalizedAdminRefreshToken || null,
          refreshTokenExpiresAt: normalizedAdminRefreshTokenExpiresAt,
        },
      },
    });
  } catch (error) {
    persistValue('asi:user', null);
    persistValue('asi:token', null);
    persistValue('asi:token-expires-at', null);
    persistValue('asi:refresh-token', null);
    persistValue('asi:refresh-token-expires-at', null);
    persistValue('asi:admin-user', null);
    persistValue('asi:admin-token', null);
    persistValue('asi:admin-token-expires-at', null);
    persistValue('asi:admin-refresh-token', null);
    persistValue('asi:admin-refresh-token-expires-at', null);
    clearAuthToken();
    dispatch({
      type: AUTH_HYDRATE,
      payload: { user: null, token: null, admin: { user: null, token: null } },
    });
  }
};

export const loginUser = (credentials) => async (dispatch) => {
  const response = await authApi.login(credentials);
  const data = extractResponseData(response);
  const normalizedToken = normalizeTokenValue(data && data.token);
  const normalizedRefreshToken = normalizeTokenValue(data && data.refreshToken);
  if (data && normalizedToken && data.user) {
    dispatch(setSession(data.user, data.token, data.refreshToken));
  }
  if (!data) {
    return data;
  }
  return {
    ...data,
    token: normalizedToken || null,
    refreshToken: normalizedRefreshToken || null,
  };
};

export const verifyUserRegistration = (payload) => async (dispatch) => {
  const response = await authApi.verifyRegistration(payload);
  const data = extractResponseData(response);
  const normalizedToken = normalizeTokenValue(data && data.token);
  const normalizedRefreshToken = normalizeTokenValue(data && data.refreshToken);
  if (data && normalizedToken && data.user) {
    dispatch(setSession(data.user, data.token, data.refreshToken));
  }
  if (!data) {
    return data;
  }
  return {
    ...data,
    token: normalizedToken || null,
    refreshToken: normalizedRefreshToken || null,
  };
};

export const resendUserRegistrationVerification = (payload) => async () => {
  const response = await authApi.resendRegistrationVerification(payload);
  return extractResponseData(response);
};

export const registerUser = (payload) => async () => {
  const response = await authApi.register(payload);
  return extractResponseData(response);
};

export const requestPasswordReset = (payload) => async () => {
  const response = await authApi.requestPasswordReset(payload);
  return extractResponseData(response);
};

export const resetPassword = (payload) => async () => {
  const response = await authApi.resetPassword(payload);
  return extractResponseData(response);
};

export const adminLogin = (credentials) => async (dispatch) => {
  const response = await authApi.adminLogin(credentials);
  const data = extractResponseData(response);
  const normalizedToken = normalizeTokenValue(data && data.token);
  const normalizedRefreshToken = normalizeTokenValue(data && data.refreshToken);
  if (data && normalizedToken && data.user) {
    dispatch(setAdminSession(data.user, data.token, data.refreshToken));
  }
  if (!data) {
    return data;
  }
  return {
    ...data,
    token: normalizedToken || null,
    refreshToken: normalizedRefreshToken || null,
  };
};

const refreshInFlight = {
  admin: null,
  user: null,
};

export function createSessionRefreshHandler(store) {
  return async (requestConfig = {}) => {
    const state = store.getState();
    const auth = state && state.auth ? state.auth : {};
    const requestUrl = requestConfig.url || '';
    const isAdminRequest = requestUrl.indexOf('/admin') === 0;
    const refreshKey = isAdminRequest ? 'admin' : 'user';
    const session = isAdminRequest ? auth.admin : auth;

    if (!session || !session.token) {
      return null;
    }

    if (!isTokenExpiring(session.tokenExpiresAt)) {
      return null;
    }

    if (!session.refreshToken || isTokenExpired(session.refreshTokenExpiresAt)) {
      throw new Error('Session expired');
    }

    if (!refreshInFlight[refreshKey]) {
      refreshInFlight[refreshKey] = authApi.refreshSession({ refreshToken: session.refreshToken })
        .then((response) => {
          const data = extractResponseData(response);
          const nextToken = normalizeTokenValue(data && data.token);
          if (!data || !nextToken || !data.user) {
            throw new Error('Unable to refresh session');
          }

          if ((data.user.kind === 'admin') || isAdminRequest) {
            store.dispatch(setAdminSession(data.user, data.token, data.refreshToken));
          } else {
            store.dispatch(setSession(data.user, data.token, data.refreshToken));
          }

          return nextToken;
        })
        .finally(() => {
          refreshInFlight[refreshKey] = null;
        });
    }

    return refreshInFlight[refreshKey];
  };
}
